import type {
  CatalogVideoModel,
  Job,
  VideoTalkingHeadRequest as Render,
} from "../../../../api/generated/model";
import { VideoTalkingHeadRequest } from "../../../../api/generated/zod";
import { MIN_SCRIPT, lengthProblem, type Cast, type TakeState } from "./cast";
import type { SpeechAudio } from "./speech";
import { useGroupedStarts, type ActorStart } from "./use-priced-starts";

/** Live's simple form's resolution (`schema.ts`): 720p, else 480p, else the model's default. */
const PREFERRED = ["720p", "480p"];

export function resolutionOf(model: CatalogVideoModel | undefined): string | null {
  const resolutions = model?.modes["video.talking_head"]?.resolutions;
  if (resolutions === undefined || resolutions === null) return null;
  return PREFERRED.find((each) => resolutions.values.includes(each)) ?? resolutions.default;
}

/**
 * What the actors speak: each its voiceover (text to speech), or one audio for them all, a
 * library audio (speech to speech, live's `audioAssetId`).
 */
export type Speaking =
  | { mode: "tts"; takes: readonly TakeState[]; script: string }
  | { mode: "sts"; speech: SpeechAudio | null };

/**
 * The audio against the model's bound (Swift's 5 to 14.8 s), in live's words: each voiceover,
 * named by its actor, or the one speech.
 */
export function lengthProblems(
  model: CatalogVideoModel | undefined,
  casts: readonly Cast[],
  speaking: Speaking,
): string[] {
  const caps = model?.modes["video.talking_head"]?.talking_head ?? null;
  const limits = {
    min_seconds: caps?.min_audio_seconds ?? null,
    max_seconds: caps?.max_audio_seconds ?? null,
  };
  if (speaking.mode === "sts") {
    const problem = speaking.speech === null ? null : lengthProblem(speaking.speech.durationMs, limits);
    return problem === null ? [] : [problem];
  }
  return casts.flatMap((cast, at) => {
    const take = speaking.takes[at];
    if (take?.state !== "done") return [];
    const problem = lengthProblem(take.durationMs, limits);
    return problem === null ? [] : [`${cast.actor.name}: ${problem}`];
  });
}

/**
 * Each actor's video request, or none until live's rules hold: an actor at least; with text to
 * speech a script of ten characters ("Script is required") and every voiceover made ("All
 * avatars must have audio generated"); with speech to speech the speech ("Speech audio is
 * required"); and no audio outside the model's bound.
 */
export function rendersOf(
  model: CatalogVideoModel | undefined,
  casts: readonly Cast[],
  speaking: Speaking,
): ActorStart<Render>[] | undefined {
  if (model === undefined || casts.length === 0) return undefined;
  if (speaking.mode === "tts" && speaking.script.trim().length < MIN_SCRIPT) return undefined;
  if (speaking.mode === "sts" && speaking.speech === null) return undefined;
  if (lengthProblems(model, casts, speaking).length > 0) return undefined;
  const starts: ActorStart<Render>[] = [];
  for (const [at, cast] of casts.entries()) {
    let audio;
    if (speaking.mode === "sts") {
      audio = { kind: "asset", asset_id: speaking.speech!.assetId };
    } else {
      if (speaking.takes[at]?.state !== "done" || cast.take === null) return undefined;
      audio = { kind: "job_output", job_id: cast.take, output_index: 0 };
    }
    const result = VideoTalkingHeadRequest.safeParse({
      type: "video.talking_head",
      model: model.id,
      actor: cast.actor.source,
      source_audio: audio,
    });
    if (!result.success) return undefined;
    starts.push({ actorId: cast.actor.key, data: result.data });
  }
  return starts;
}

export interface RendersOptions {
  teamId: string;
  brandId: string;
  model: CatalogVideoModel | undefined;
  casts: readonly Cast[];
  speaking: Speaking;
  onStarted: (job: Job) => void;
}

/**
 * Live's start (G8-talking-head, PR B): one `video.talking_head` job per actor, its portrait
 * speaking its voiceover or the one speech, at the server's resolution (live's 720p, else 480p),
 * each priced by its audio's length, one `group` for them all. Live checks Swift's bound only on
 * an upload (bug 73); ours holds every audio to it, in live's words.
 */
export function useRenders({ teamId, brandId, model, casts, speaking, onStarted }: RendersOptions) {
  const step = useGroupedStarts({
    teamId,
    brandId,
    starts: rendersOf(model, casts, speaking),
    onStarted,
  });
  return {
    ...step,
    resolution: resolutionOf(model),
    problems: lengthProblems(model, casts, speaking),
  };
}
